// 懒加载 chunk 加载失败的"自动刷新"兜底逻辑。
const RELOAD_MARK_KEY = "__chunk_reload_ts__";

// 刚自动刷新过、又在该时间窗内再次失败 → 判定为"刷了也没用"，不再刷。
const RELOAD_WINDOW_MS = 20_000;

// QA / 测试复现失败卡片用：URL 带 ?__chunkErrTest=1 时，让懒加载路由直接进失败卡片
const FORCE_TEST_PARAM = "__chunkErrTest";

// QA / 测试复现"自动刷新"用：URL 带 ?__chunkReloadTest=1 时，模拟
const FORCE_RELOAD_ONCE_PARAM = "__chunkReloadTest";
// 标记 reload-once 测试"本轮已注入过一次失败"，刷新后据此放行正常加载。
const RELOAD_ONCE_FLAG_KEY = "__chunk_reload_once_done__";

// 调试/复现开关：开启 ?__chunkErrTest / ?__chunkReloadTest 两个 URL 测试参数。
// 验证通过后想关闭：把 true 改成 false（一行搞定，生产也不会再触发）。
// 设为 false 后是编译期常量，相关测试分支会被 tree-shaking 整段移除，零残留。
const ENABLE_CHUNK_TEST_HOOKS = false;

// 覆盖 Vite / 各浏览器对"动态 import / CSS 预加载失败"的常见报错措辞。
const CHUNK_ERROR_RE =
  /Loading chunk|Loading CSS chunk|dynamically imported module|Unable to preload|Importing a module script failed|error loading dynamically imported/i;

/**
 * 判断一个错误是否属于"chunk 加载失败"。
 * 只有这类错误才走自动刷新；其它错误一律不碰。
 */
export function isChunkLoadError(err: unknown): boolean {
  if (!err) return false;
  const anyErr = err as { message?: string; name?: string };
  const msg = anyErr.message || String(err);
  const name = anyErr.name || "";
  return name === "ChunkLoadError" || CHUNK_ERROR_RE.test(msg);
}

/**
 * 是否处于"强制失败卡片"测试模式（URL 带 ?__chunkErrTest=1）。
 */
export function isForceChunkErrorTest(): boolean {
  if (!ENABLE_CHUNK_TEST_HOOKS) return false;
  try {
    return new URLSearchParams(window.location.search).has(FORCE_TEST_PARAM);
  } catch {
    return false;
  }
}

/**
 * 是否处于"模拟自动刷新"测试模式（URL 带 ?__chunkReloadTest=1）。
 */
export function isForceReloadOnceTest(): boolean {
  if (!ENABLE_CHUNK_TEST_HOOKS) return false;
  try {
    return new URLSearchParams(window.location.search).has(
      FORCE_RELOAD_ONCE_PARAM
    );
  } catch {
    return false;
  }
}

/** reload-once 测试：本轮是否已经注入过一次失败（刷新后为 true）。 */
export function reloadOnceTestAlreadyFailed(): boolean {
  try {
    return sessionStorage.getItem(RELOAD_ONCE_FLAG_KEY) === "1";
  } catch {
    return false;
  }
}

/** reload-once 测试：标记"已注入一次失败"，随刷新带到下一次加载。 */
export function markReloadOnceTestFailed(): void {
  try {
    sessionStorage.setItem(RELOAD_ONCE_FLAG_KEY, "1");
  } catch {
    // ignore
  }
}

/** reload-once 测试：清除标记（加载成功后调用，便于重复演示）。 */
export function clearReloadOnceTestFlag(): void {
  try {
    sessionStorage.removeItem(RELOAD_ONCE_FLAG_KEY);
  } catch {
    // ignore
  }
}

/**
 * 只读判断：当前是否允许自动刷新（不产生任何副作用）。
 *  - 无标记 → 允许（首次故障）
 *  - 标记在时间窗内 → 不允许（刚刷过还失败，防死循环）
 *  - 标记已超时间窗 → 允许（视为后续独立故障）
 */
export function canAutoReload(): boolean {
  try {
    const raw = sessionStorage.getItem(RELOAD_MARK_KEY);
    if (!raw) return true;
    const ts = parseInt(raw, 10);
    if (!ts) return true;
    return Date.now() - ts >= RELOAD_WINDOW_MS;
  } catch {
    return true;
  }
}

/**
 * 写入刷新标记并整页刷新。
 */
export function markAndReload(): void {
  try {
    sessionStorage.setItem(RELOAD_MARK_KEY, String(Date.now()));
  } catch {
    // sessionStorage 不可用时也要保证能刷新
  }
  window.location.reload();
}

/**
 * chunk 成功加载后清除标记，释放后续故障的刷新预算。
 */
export function clearReloadMark(): void {
  try {
    sessionStorage.removeItem(RELOAD_MARK_KEY);
  } catch {
    // ignore
  }
}
